import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import config from '../aws-config';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Grid,
} from '@mui/material';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    completeNewPassword,
    clearPendingChallenge,
    forgotPassword,
    confirmPasswordReset,
    isAuthenticated,
  } = useAuthContext();
  const [mode, setMode] = useState('login'); // login | newPassword | forgot | reset
  const [form, setForm] = useState({
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    code: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.from?.pathname || '/', { replace: true });
    }
  }, [isAuthenticated, location.state, navigate]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const resetMessages = () => {
    setError('');
    setInfo('');
  };

  const validatePassword = (password, confirm) => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters.';
    }
    if (password !== confirm) {
      return 'Passwords do not match.';
    }
    return null;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    resetMessages();

    try {
      await login(form.username, form.password);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      if (err.challenge === 'NEW_PASSWORD') {
        setMode('newPassword');
        setInfo('Please set a permanent password to continue.');
      } else {
        setError(err.message || 'Unable to sign in.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = async (event) => {
    event.preventDefault();
    resetMessages();

    const validation = validatePassword(form.newPassword, form.confirmPassword);
    if (validation) {
      setError(validation);
      return;
    }

    setSubmitting(true);
    try {
      await completeNewPassword(form.newPassword);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to set new password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    resetMessages();

    try {
      await forgotPassword(form.username);
      setMode('reset');
      setInfo('Check your email for the verification code.');
    } catch (err) {
      setError(err.message || 'Failed to request password reset.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    resetMessages();

    const validation = validatePassword(form.newPassword, form.confirmPassword);
    if (validation) {
      setError(validation);
      return;
    }
    if (!form.code.trim()) {
      setError('Verification code is required.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(form.username, form.code, form.newPassword);
      setMode('login');
      setInfo('Password reset successful. You can now sign in.');
      setForm((current) => ({ ...current, password: '', newPassword: '', confirmPassword: '', code: '' }));
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    resetMessages();
  };

  const titles = {
    login: 'Sign in',
    newPassword: 'Set new password',
    forgot: 'Reset password',
    reset: 'Enter verification code',
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Bcknd CMS
        </Typography>
        <Typography component="p" color="text.secondary" sx={{ mt: 1 }}>
          {titles[mode]}
        </Typography>
        <Box
          component="form"
          onSubmit={mode === 'newPassword' ? handleNewPassword : mode === 'forgot' ? handleForgot : mode === 'reset' ? handleReset : handleLogin}
          sx={{ mt: 3, width: '100%' }}
        >
          <Grid container spacing={2}>
            {!config.userPoolId || !config.clientId || !config.apiEndpoint ? (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Runtime CMS configuration is incomplete. Populate <code>window.__CMS_CONFIG__</code> or Vite environment variables.
                </Alert>
              </Grid>
            ) : null}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            {info && (
              <Grid item xs={12}>
                <Alert severity="info">{info}</Alert>
              </Grid>
            )}

            {(mode === 'login' || mode === 'forgot') && (
              <Grid item xs={12}>
                <TextField
                  type="email"
                  label="Email"
                  value={form.username}
                  onChange={updateField('username')}
                  required
                  fullWidth
                  autoFocus
                />
              </Grid>
            )}

            {mode === 'login' && (
              <Grid item xs={12}>
                <TextField
                  type="password"
                  label="Password"
                  value={form.password}
                  onChange={updateField('password')}
                  required
                  fullWidth
                />
              </Grid>
            )}

            {(mode === 'newPassword' || mode === 'reset') && (
              <>
                {mode === 'reset' && (
                  <Grid item xs={12}>
                    <TextField
                      type="text"
                      label="Verification code"
                      value={form.code}
                      onChange={updateField('code')}
                      required
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    type="password"
                    label="New password"
                    value={form.newPassword}
                    onChange={updateField('newPassword')}
                    required
                    fullWidth
                    inputProps={{ minLength: 12 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    type="password"
                    label="Confirm new password"
                    value={form.confirmPassword}
                    onChange={updateField('confirmPassword')}
                    required
                    fullWidth
                    inputProps={{ minLength: 12 }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (
              mode === 'login'
                ? 'Sign in'
                : mode === 'newPassword'
                  ? 'Set password'
                  : mode === 'forgot'
                    ? 'Send code'
                    : 'Reset password'
            )}
          </Button>

          <Grid container justifyContent="flex-end">
            {mode === 'login' && (
              <Grid item>
                <Link component="button" variant="body2" onClick={() => switchMode('forgot')}>
                  Forgot password?
                </Link>
              </Grid>
            )}
            {(mode === 'newPassword' || mode === 'forgot' || mode === 'reset') && (
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    clearPendingChallenge();
                    switchMode('login');
                  }}
                  disabled={submitting}
                >
                  Back to sign in
                </Link>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}